import { SlateNode } from '@all-hands-hq/plex-graphql-schema';
import {
  pipe,
  toggleList,
  unwrapList,
  withAutoformat,
  withInlineVoid,
  withLink,
  withList,
} from '@udecode/slate-plugins';
import lodash from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createEditor, Editor } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, withReact } from 'slate-react';

import { Entities, EntitiesContext } from './entities';
import { SlateEditableWithControls } from './internal/editor';
import { SlatePluginOptions, SlatePlugins } from './internal/plugins';
import { useSyncEntitiesToEditor, withEntities } from './internal/withEntities';
import { withFiles } from './internal/withLinks';
import { withMentions } from './internal/withMentions';
import { withVoidElements } from './internal/withPlexLinks';
import { MentionData } from './mentions/MentionsMenu';
import { usePlexUserSearch } from './mentions/usePlexUserSearch';

const NEW_SLATE: SlateNode[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export class SlateEditorValue {
  private _value: SlateNode[];

  private constructor(value: SlateNode[]) {
    this._value = value;
  }

  get value(): SlateNode[] {
    return this._value;
  }

  static wrapCallback = (onChange: (slateEditorValue: SlateEditorValue) => void) => (value: SlateNode[]): void => {
    onChange(new SlateEditorValue(value));
  };

  static from(value: SlateNode[]): SlateEditorValue {
    // Slate relies on in-memory object references to determine what values represent the same DOM elements
    // hence one needs to ensure Editable slate containers use deeply cloned objects
    // https://github.com/ianstormtaylor/slate/pull/3506#issuecomment-616238475
    // https://github.com/ianstormtaylor/slate/issues/3380#issuecomment-612432934
    return new SlateEditorValue(cloneDeep(value));
  }

  static new(): SlateEditorValue {
    return SlateEditorValue.from(NEW_SLATE);
  }

  isEmpty(): boolean {
    return lodash.isEqual(this._value, NEW_SLATE);
  }
}

type EditorProps = {
  value: SlateEditorValue;
  initialEntities: Entities;
  onChange: (value: SlateEditorValue) => void;
  title?: string;
  className?: string;
  primaryLink: string | null | undefined;
  onChangePrimaryLink: (primaryLink: string) => void;
  hideControls?: boolean;
};

const preFormat = (editor: Editor): void => unwrapList(editor, SlatePluginOptions);

export function SlateEditor({
  value: slateEditorValue,
  initialEntities,
  onChange,
  primaryLink,
  onChangePrimaryLink,
  title,
  className,
  hideControls,
}: EditorProps): React.ReactElement {
  const [mentionData, setMentionData] = useState<MentionData>({
    target: undefined,
    index: 0,
  });
  const [entities, setEntities] = useState<Entities>(initialEntities);
  const entitiesRef = useRef(entities);

  const { setQuery, search } = usePlexUserSearch({ onlySignedUpUsers: true, minimumQueryLength: 0 });

  const editor = useMemo(
    () =>
      pipe(
        createEditor(),
        withReact,
        withEntities(setEntities, entitiesRef),
        withMentions(setMentionData, setQuery),
        withFiles,
        withHistory,
        withVoidElements,
        withList({ ...SlatePluginOptions, validLiChildrenTypes: ['paragraph', 'list-item'] }),
        withLink({
          ...SlatePluginOptions,
          rangeBeforeOptions: {
            // Ensure lookup does not search across other blocks
            multiPaths: false,
          },
        }),
        withInlineVoid({ plugins: SlatePlugins }),
        withAutoformat({
          rules: [
            {
              type: 'italic',
              between: ['_', '_'],
              mode: 'inline',
              insertTrigger: true,
            },
            {
              type: 'bold',
              between: ['*', '*'],
              mode: 'inline',
              insertTrigger: true,
            },
            {
              type: 'code',
              between: ['`', '`'],
              mode: 'inline',
              insertTrigger: true,
            },
            {
              type: 'list-item',
              markup: ['*', '-'],
              preFormat,
              format: (editor): void => {
                toggleList(editor, { ...SlatePluginOptions, typeList: 'bulleted-list' });
              },
            },
            {
              type: 'list-item',
              markup: ['1.', '1)'],
              preFormat,
              format: (editor): void => {
                toggleList(editor, { ...SlatePluginOptions, typeList: 'numbered-list' });
              },
            },
            {
              type: 'block-quote',
              markup: ['>'],
              preFormat,
            },
          ],
        })
      ),
    [setQuery]
  );

  useSyncEntitiesToEditor(editor, entities);

  const handleChange = useCallback((value: SlateNode[]) => SlateEditorValue.wrapCallback(onChange)(value), [onChange]);
  const { value } = slateEditorValue;
  return (
    // TODO(https://github.com/all-hands-hq/source/issues/1046): use `Slate<SlateElement>`
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Slate editor={editor} value={value} onChange={handleChange}>
      <EntitiesContext.Provider value={entities}>
        <SlateEditableWithControls
          title={title}
          className={className}
          primaryLink={primaryLink}
          onChangePrimaryLink={onChangePrimaryLink}
          mentionData={mentionData}
          setMentionData={setMentionData}
          search={search}
          hideControls={hideControls}
        />
      </EntitiesContext.Provider>
    </Slate>
  );
}
